import { TrendingUp, TrendingDown, Utensils } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";

export function DishPerformanceTable({
  type,
  topProfitableDishes = [],
  lossMakingDishes = [],
}) {
  const dishes = type === "profitable" ? topProfitableDishes : lossMakingDishes;
  const title =
    type === "profitable" ? "Top Profitable Dishes" : "Low Margin Dishes";
  const description =
    type === "profitable"
      ? "High margin performers (>30%)"
      : "Items needing attention (<30%)";

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-foreground text-lg">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          </div>
          <Badge variant="outline" className="text-xs">
            {dishes.length} items
          </Badge>
        </div>
      </div>

      {/* Table */}
      {dishes && dishes.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground font-semibold">
                  Dish Name
                </TableHead>
                <TableHead className="text-muted-foreground text-right font-semibold">
                  Qty
                </TableHead>
                <TableHead className="text-muted-foreground text-right font-semibold">
                  Revenue
                </TableHead>
                <TableHead className="text-muted-foreground text-right font-semibold">
                  Cost
                </TableHead>
                <TableHead className="text-muted-foreground text-right font-semibold">
                  Profit
                </TableHead>
                <TableHead className="text-muted-foreground text-right font-semibold">
                  Margin
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dishes.map((dish, index) => (
                <TableRow
                  key={index}
                  className="border-border hover:bg-muted/30 transition-colors"
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold",
                          type === "profitable"
                            ? "bg-success/10 text-success"
                            : "bg-warning/10 text-warning",
                        )}
                      >
                        {index + 1}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground">
                          {dish.name}
                        </span>
                        {dish.category && (
                          <span className="text-xs text-muted-foreground">
                            {dish.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-medium text-foreground">
                      {dish.soldQty || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-medium text-foreground">
                      ₹{(dish.revenue || 0).toLocaleString("en-IN")}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-muted-foreground">
                      ₹{(dish.cost || 0).toLocaleString("en-IN")}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span
                      className={cn(
                        "font-semibold",
                        type === "profitable" ? "text-success" : "text-warning",
                      )}
                    >
                      ₹{(dish.profit || 0).toLocaleString("en-IN")}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          "font-semibold",
                          dish.margin >= 30 &&
                            "bg-success/20 text-success border-success/30",
                          dish.margin < 30 &&
                            dish.margin >= 20 &&
                            "bg-warning/20 text-warning border-warning/30",
                          dish.margin < 20 &&
                            "bg-destructive/20 text-destructive border-destructive/30",
                        )}
                      >
                        {(dish.margin || 0).toFixed(1)}%
                      </Badge>

                      {dish.trend === "up" && (
                        <TrendingUp className="w-4 h-4 text-success" />
                      )}
                      {dish.trend === "down" && (
                        <TrendingDown className="w-4 h-4 text-destructive" />
                      )}
                      {dish.trend === "stable" && (
                        <div className="w-4 h-4 flex items-center justify-center text-muted-foreground">
                          —
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="p-12 text-center text-muted-foreground">
          <Utensils className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No dishes in this category</p>
          <p className="text-sm mt-1">
            {type === "profitable"
              ? "Start selling dishes to see top performers"
              : "All dishes are performing well!"}
          </p>
        </div>
      )}
    </div>
  );
}
